import type ru from '../ru'
import type { DeepPartial } from '../../merge'

/**
 * samly.ge copy that differs from the Uzbek site. Everything else comes from ../en.
 * Claims here must match what Samly really offers in Georgia.
 */
const overrides: DeepPartial<typeof ru> = {
  hero: {
    stat_country: 'Теперь и в Грузии',
    stat_payments: 'Наличные · карта · QR',
    dashboard: {
      url: 'samly.ge/dashboard',
      currency: 'лари',
    },
  },
  logos: {
    formats: {
      oshkhona: 'Хинкальные',
    },
  },
  painpoints: {
    before: [
      'Цену подняли в кассе — на витрине доставки осталась старая',
      'Хачапури закончились, а заказы на них продолжают приходить',
      'Склад в Excel, касса отдельно, доставка у агрегатора',
      'Официант обещает гостю блюдо, которого нет на кухне',
      'Чтобы собрать отчёт, нужно сводить три выгрузки вручную',
    ],
  },
  onemenu: {
    moments: [
      {
        title: 'Измените цену один раз',
        desc: 'Новая цена сразу на кассе, в QR-меню, на витрине доставки, у официанта и на экранах. Дублировать ничего не нужно.',
      },
      {
        title: 'Продайте последнюю порцию',
        desc: 'Блюдо уходит в стоп-лист в ту же секунду: исчезает с витрины, гость его больше не увидит, официант не сможет его пробить.',
      },
      {
        title: 'Склад считает ингредиенты',
        desc: 'У каждого блюда есть технологическая карта. Продали хачапури — со склада списались мука, сулугуни и мацони. Остаток настоящий, а не «примерно».',
      },
    ],
  },
  stats: {
    items: [
      { val: '20+', label: 'заведений', sub: 'работают на Samly' },
      { val: '1 месяц', label: 'бесплатно', sub: 'полный набор, без карты' },
      { val: '8', label: 'компонентов', sub: 'платите только за свои' },
      { val: '−30%', label: 'при оплате за год', sub: 'минимальный срок — 3 месяца' },
    ],
  },
  features: {
    mock: {
      warehouse_sold: 'Продан 1 хачапури',
      currency: 'лари',
      pay_click: 'Оплатить картой',
      pay_payme: 'Позвать официанта',
      dishes: {
        plov: 'Хачапури',
        lagman: 'Хинкали',
        tea: 'Чай',
        shashlik: 'Мцвади',
        manti: 'Лобиани',
        soup: 'Харчо',
        salad: 'Салат',
      },
      ingredients: {
        rice: 'Мука',
        oil: 'Мацони',
        meat: 'Сулугуни',
        carrot: 'Яйца',
      },
    },
    pos: {
      points: [
        'Наличные, карта и QR в одном окне',
        'Возвраты, скидки и удаления — только по PIN менеджера',
        'Смены и расхождения по кассе под контролем',
        'Чеки на любом принтере — ESC/POS или StarPRNT',
        'Работает без интернета, синхронизируется после',
      ],
    },
    storefront: {
      points: [
        'Telegram Mini App, сайт и мобильное приложение',
        'Свой домен — гость видит ваш бренд, а не чужой',
        'Оплата наличными или картой при получении',
        'Заказ приходит сразу в кассу и на кухню',
        'Комиссию агрегатора за свои заказы вы не платите',
      ],
    },
    qr: {
      points: [
        'QR на столе — меню открывается в браузере, без приложения',
        'Общая корзина на весь стол',
        'Заказ уходит сразу на кухню',
        'То же меню и те же остатки, что в кассе',
        'Гость видит статус заказа и не переспрашивает',
      ],
    },
    warehouse: {
      points: [
        'Технологическая карта на каждое блюдо',
        'Продали хачапури — списались мука, сулугуни и мацони',
        'Себестоимость и маржа по каждой позиции',
        'Стоп-лист формируется сам, когда ингредиент кончился',
        'Несколько складов, если кухонь больше одной',
      ],
    },
  },
  integrations: {
    subtitle: 'Оплата, доставка и оборудование — из коробки.',
    groups: {
      // No Georgian partner is signed yet (29.09.2026): all of them carry the `soon` badge. Drop it once a deal is live.
      payment: {
        title: 'Оплата',
        items: [
          { name: 'Наличные, карта, QR', desc: 'Любой банковский терминал на кассе' },
          { name: 'TBC Pay', logo: '/partners/tbc.svg', desc: 'Планируем подключить', soon: true },
          { name: 'Bank of Georgia', logo: '/partners/bank-of-georgia.svg', desc: 'Планируем подключить', soon: true },
        ],
      },
      delivery: {
        title: 'Доставка',
        items: [
          { name: 'Своя витрина', desc: 'Telegram, сайт, приложение' },
          { name: 'Wolt', logo: '/partners/wolt.png', desc: 'Планируем подключить', soon: true },
          { name: 'Glovo', logo: '/partners/glovo.png', desc: 'Планируем подключить', soon: true },
        ],
      },
      local: {
        title: 'Платформа',
        items: [
          { name: 'Android / iOS', desc: 'Любые устройства' },
          { name: 'Open API', desc: 'Свои интеграции, входит в подписку' },
        ],
      },
    },
  },
  dashboardPreview: {
    owner: {
      mockup: {
        revenue: 'лари',
        branches: {
          chilanzar: 'Ваке',
          yunusabad: 'Сабуртало',
          samarkand: 'Батуми',
        },
      },
    },
    qr: {
      features: {
        payments: 'Заказ попадает на кухню за секунды',
      },
    },
    monitors: {
      mockup: {
        items: {
          lagman: 'Хинкали',
          plov: 'Хачапури',
          shashlik: 'Мцвади',
          manti: 'Лобиани',
        },
      },
    },
  },
  components: {
    paid_note: 'Цена за месяц. Своя доставка, приложение официанта и QR-меню покупаются на каждый филиал.',
    per_month: 'лари/мес',
  },
  pricing: {
    subtitle: 'Цена — сумма компонентов. Минимальный срок — 3 месяца.',
    per_month: 'лари/мес',
    receipt_total_sub: 'лари за {{months}} мес',
    rules: [
      'Цена — сумма компонентов. Тарифов нет.',
      'Минимальный срок покупки — 3 месяца.',
      'Скидка за срок: 3 месяца −10%, 6 месяцев −20%, 12 месяцев −30%.',
      'Итог всегда округляется в меньшую сторону.',
      'Минимум: 1 филиал, 1 сотрудник, 1 POS-устройство.',
      'Своя доставка, приложение официанта и QR-меню — на каждый филиал, не больше числа филиалов.',
    ],
  },
  faq: {
    items: [
      {
        q: 'Как считается цена?',
        a: 'Тарифов нет. Вы собираете набор из восьми компонентов, и цена — их сумма. Минимальный срок покупки — 3 месяца, скидка за срок: 3 месяца −10%, 6 месяцев −20%, 12 месяцев −30%. Минимальный набор — 1 филиал, 1 сотрудник и 1 POS-устройство.',
      },
      {
        q: 'Что входит в подписку без доплат?',
        a: 'Кухня (KDS), приложение менеджера, лояльность и CRM, AI-аналитика, киоск самообслуживания, Open API, стоп-лист, смены и Z-отчёты, план зала, PIN на возвраты, push владельцу и печать чеков. Платить нужно только за филиалы, сотрудников, POS-устройства, свою доставку, приложение официанта, QR-меню, экраны и склады.',
      },
      {
        q: 'Правда ли, что склад считает ингредиенты, а не порции?',
        a: 'Да. У каждого блюда есть технологическая карта. Когда вы продаёте хачапури, со склада списываются мука, сулугуни и мацони в указанных количествах. Поэтому остаток настоящий, себестоимость считается сама, а стоп-лист формируется автоматически — как только кончился любой ингредиент.',
      },
      {
        q: 'Как работает своя доставка?',
        a: 'Вы получаете собственную витрину: Telegram Mini App, сайт и мобильное приложение, при желании на своём домене. Заказ попадает сразу в кассу и на кухню, на том же меню и тех же остатках. Комиссию агрегатора за такие заказы вы не платите.',
      },
      {
        q: 'Сколько занимает внедрение?',
        a: 'До недели. Настройку делает наша команда: переносим меню, заводим технологические карты, подключаем оборудование и обучаем сотрудников. Онбординг бесплатный.',
      },
      {
        q: 'Нужно ли покупать оборудование?',
        a: 'Если у вас его ещё нет — да. Моноблок для кассы, чековый принтер, экраны для кухни и меню покупаются отдельно. Это разовая статья, и суммы очень разные, поэтому мы не включаем её в подписку. Подскажем, что именно нужно вашему залу, и подберём под бюджет. Samly работает на Android и iOS, так что часть можно закрыть обычным планшетом.',
      },
      {
        q: 'Работает ли без интернета?',
        a: 'Касса и кухонный экран работают офлайн. Экраны меню могут крутить контент с USB-накопителя. Когда интернет возвращается, данные синхронизируются сами — ни один заказ не теряется.',
      },
      {
        q: 'Что даёт бесплатный месяц?',
        a: 'Полный набор компонентов на месяц, без карты и без обязательств. Если после этого остаётесь, дальше действует минимальный срок в 3 месяца.',
      },
      {
        // TODO(ge): the Samly apps have no Georgian UI yet (ru/uz/en only). Update once it ships.
        q: 'На каких языках доступен Samly?',
        a: 'Приложения Samly доступны на русском и английском языках. Поддержка отвечает на русском и английском по телефону и в Telegram — ежедневно, кроме воскресенья, с 09:00 до 21:00 по тбилисскому времени.',
      },
    ],
  },
  cta: {
    trust_payment: 'Наличные · карта · QR',
    trust_local: 'Теперь и в Грузии',
  },
  contact: {
    hours: 'Пн–Сб, 09:00–21:00 (тбилисское время)',
  },
}

export default overrides
